import clsx from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";

const Pagination = (props: {
  pageCount: number;
  currentPage: number; 
  pageLimit: number;
  onPageChange?: (page: number) => void;
  onPreloadPage?: (page: number) => void;
}) => {
  const {
    pageCount = 20,
    currentPage: _currentPage = 1,
    pageLimit = 5,
    onPageChange,
    onPreloadPage,
  } = props;

  const [currentPage, setCurrentPage] = useState(+_currentPage);

  // Compute which group of pages we’re in
  const totalGroups = Math.ceil(pageCount / pageLimit);

  const currentGroup = Math.ceil(currentPage / pageLimit);

  // Compute first/last page in this group
  const startPage = (currentGroup - 1) * pageLimit + 1;
  const endPage = Math.min(startPage + pageLimit - 1, pageCount);

  const handleClickPage = (page: number) => {
    let newPage = page;
    // if click on the last visible page and there *is* a next group, jump into it
    if (page === endPage && currentGroup < totalGroups) {
      newPage = endPage + 1;
    }
    setCurrentPage(newPage);
    onPageChange?.(newPage);
  };

  const handlePrev = () => {
    if (currentGroup === 1) return;
    const prevPage = startPage - 1;
    setCurrentPage(prevPage);
    onPageChange?.(prevPage);
  };

  const handleNext = () => {
    if (currentGroup === totalGroups) return;
    const nextPage = endPage + 1;
    setCurrentPage(nextPage);
    onPageChange?.(nextPage);
  };

  // build the array of visible pages
  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <nav className="flex justify-center gap-x-2.5 mt-8 xl:mt-32">
      <button
        onClick={handlePrev}
        disabled={currentGroup === 1}
        className="rounded-full text-white w-7 xl:w-12 h-7 xl:h-12 grid place-items-center border-2 border-white cursor-pointer disabled:cursor-no-drop"
      >
        <ChevronLeft strokeWidth={3} />
      </button>

      {pages.map((pageNumber, idx) => (
        <button
          key={idx}
          className={clsx(
            "rounded-full w-7 xl:w-12 h-7 xl:h-12 flex justify-center items-center border-2 border-white bg-white cursor-pointer text-darkgold xl:text-3xl",
            {
              "!bg-gold !border-0": currentPage === pageNumber,
              "!text-white": currentPage === pageNumber,
            }
          )}
          onClick={() => handleClickPage(pageNumber)}
        >
          {pageNumber}
        </button>
      ))}

      <button
        onClick={handleNext}
        disabled={currentGroup === totalGroups}
        className="rounded-full text-white w-7 xl:w-12 h-7 xl:h-12 grid place-items-center border-2 border-white cursor-pointer disabled:cursor-no-drop"
      >
        <ChevronRight strokeWidth={3} />
      </button>
    </nav>
  );
};

export default Pagination;
